import { useId } from 'react'
import { Field, FieldGrid, CountInput } from './fields'

/**
 * Step 4 · Impact — the counters, all optional.
 *
 * ## Why nothing here can block a submission
 *
 * A field reporter watching something happen must be able to file in under a minute.
 * Requiring a death count before a report can be submitted is how incidents go unreported:
 * the reporter does not know yet, and the honest options are to guess or to abandon the
 * form. Both are worse than an empty field that somebody fills in later.
 *
 * ## Empty is not zero
 *
 * "No deaths" and "nobody has counted" are different facts, and the difference matters to
 * anyone reading the register later. An empty box stays null; only a typed 0 means zero.
 */
export function StepImpact({
  value, onChange, errors,
}: {
  value: {
    victimCount: number | null
    injuryCount: number | null
    deathCount: number | null
    arrestsMade: number | null
    estimatedVoterImpact: number | null
  }
  onChange: (patch: Partial<typeof value>) => void
  errors: Record<string, string>
}) {
  const ids = useId()
  return (
    <div className="space-y-4">
      <p className="text-erp-sm text-muted-foreground">
        Every field on this step is optional, and the step can be skipped entirely. Leave a
        box empty where nobody has counted — an empty box says "not known", which is not
        the same as zero.
      </p>

      <FieldGrid>
        <Field label="People affected" htmlFor={`${ids}-vic`} error={errors.victimCount}>
          <CountInput id={`${ids}-vic`} value={value.victimCount}
            onChange={(v) => onChange({ victimCount: v })} />
        </Field>
        <Field label="Injuries" htmlFor={`${ids}-inj`} error={errors.injuryCount}>
          <CountInput id={`${ids}-inj`} value={value.injuryCount}
            onChange={(v) => onChange({ injuryCount: v })} />
        </Field>
        <Field label="Deaths" htmlFor={`${ids}-dth`} error={errors.deathCount}>
          <CountInput id={`${ids}-dth`} value={value.deathCount}
            onChange={(v) => onChange({ deathCount: v })} />
        </Field>
        <Field label="Arrests made" htmlFor={`${ids}-arr`} error={errors.arrestsMade}>
          <CountInput id={`${ids}-arr`} value={value.arrestsMade}
            onChange={(v) => onChange({ arrestsMade: v })} />
        </Field>
        <Field label="Voters affected (estimate)" htmlFor={`${ids}-vot`}
          error={errors.estimatedVoterImpact}
          hint="Roughly how many people could not vote, or voted under duress.">
          <CountInput id={`${ids}-vot`} value={value.estimatedVoterImpact}
            onChange={(v) => onChange({ estimatedVoterImpact: v })} />
        </Field>
      </FieldGrid>
    </div>
  )
}
